import { ReactNode, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Archive,
  ArrowRight,
  Cloud,
  Gift,
  MessageSquare,
  ShieldOff,
  Trophy,
  Users,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { Skeleton } from "../../components/Skeleton/Skeleton";

import { ApiError, NetworkError, describeError } from "../../lib/errors";
import { deletePlayer, getPlayerDeletionPreview } from "./api";
import { playerDeletionPreviewQueryKey } from "./queryKeys";
import type { DeletionGuild, Player, PlayerDeletionPreview } from "./types";
import styles from "./DeletePlayerDialog.module.css";

interface Props {
  projectId: string;
  player: Player;
  onClose: () => void;
  /**
   * Called once the player is gone from the server, with whether THIS dialog
   * is what removed them and — when it is — the guild verdict the SERVER
   * returned, so the page can leave for the players list and say what actually
   * happened. Never the preflight's prediction: those two can disagree, and
   * the one worth repeating to the operator is the one that happened.
   *
   * `deletedHere: false` is the dead end — somebody deleted the player first,
   * so there is no verdict of ours and nothing of ours to announce. The page
   * leaves without a word rather than congratulate the operator on a deletion
   * they did not perform.
   */
  onDeleted: (deletedHere: boolean, guild: DeletionGuild | null) => void;
}

/**
 * The confirmation dialog for deleting a player.
 *
 * It opens straight away and loads the preflight inside itself: the card is
 * opened to edit a player, not to delete one, so nothing is fetched until the
 * operator asks this question. Until the breakdown arrives the delete button
 * stays disabled — nobody confirms what they have not been shown — but a
 * preflight that FAILS does not block anything. It decides nothing on the
 * server; the deletion is the same operation with or without it.
 */
export function DeletePlayerDialog({
  projectId,
  player,
  onClose,
  onDeleted,
}: Props) {
  const [typed, setTyped] = useState("");

  const preview = useQuery({
    queryKey: playerDeletionPreviewQueryKey(projectId, player.id),
    queryFn: () => getPlayerDeletionPreview(projectId, player.id),
    // A preflight is a snapshot of a moment, not a cache entry: reopening the
    // dialog asks again.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => deletePlayer(projectId, player.id),
    onSuccess: (result) => onDeleted(true, result.guild),
  });

  const deleteError =
    mutation.error instanceof ApiError ? mutation.error : null;
  // A 404 means somebody else got there first — a second tab, another
  // operator. There is nothing left to confirm, so the dialog collapses into
  // the news and one way out.
  const alreadyGone = deleteError?.status === 404;

  // Typed out to confirm is the player's DISPLAY NAME — the same question the
  // guild and project dialogs ask, and the name the title above asks it about.
  //
  // Trimmed on both sides because the value is meant to be copied out of the
  // line above, and a paste that carries a trailing space is not a different
  // player. Empty never confirms: the game's own PATCH /v1/me stores
  // whatever display name it is handed, blanks included, and on such a player
  // an empty field would otherwise equal the name and arm the button with
  // nothing typed at all. DeleteProjectModal guards its own gate the same way.
  const wanted = player.display_name.trim();
  const confirmed = wanted !== "" && typed.trim() === wanted;
  const canDelete =
    confirmed && !preview.isLoading && !mutation.isPending && !alreadyGone;

  let body: ReactNode;
  if (alreadyGone) {
    body = (
      <Alert variant="danger">
        This player no longer exists — someone deleted it first.
      </Alert>
    );
  } else if (mutation.isPending) {
    body = (
      <p className={styles.modalBodyText} style={{ margin: 0 }}>
        Removing the account, its guild membership and everything hanging off
        them…
      </p>
    );
  } else {
    body = (
      <>
        <p className={styles.modalBodyText}>
          The account and everything hanging off it go for good.{" "}
          <b>This cannot be undone.</b>
        </p>

        {preview.isError ? (
          <>
            <Alert variant="danger">
              Couldn&apos;t work out what this deletion affects. Deleting still
              works and does exactly the same thing — you just won&apos;t see it
              listed first.
            </Alert>
            <div className={styles.retry}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void preview.refetch()}
                loading={preview.isFetching}
              >
                Retry
              </Button>
            </div>
          </>
        ) : preview.isLoading || !preview.data ? (
          <PreviewSkeleton />
        ) : (
          <PreviewBreakdown preview={preview.data} />
        )}

        <div className={styles.confirm}>
          {wanted === "" ? (
            // Nothing to retype, so there is no gate to pass — and a gate that
            // passes itself is worse than none: the button would arm on an
            // empty field. The deletion waits for a name instead, which is one
            // save away on the card behind this dialog.
            <span className={styles.helper}>
              This player has no display name, and the deletion is confirmed by
              typing it. Give them one first, then delete.
            </span>
          ) : (
            <>
              {/* A <span>, not a <label htmlFor>: Chrome's label activation
                  fires on mouseup inside a label and collapses the selection
                  of the very name that is here to be copied. `aria-label`
                  names the field instead — as in DeleteProjectModal,
                  RotateConfirmModal and ApplyConfirmModal. */}
              <span className={styles.modalLabel}>
                Type <code>{player.display_name}</code> to confirm
              </span>
              <input
                className={styles.input}
                aria-label={`Type ${player.display_name} to confirm`}
                autoComplete="off"
                spellCheck={false}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
              <span className={styles.helper}>
                The player&apos;s display name, exactly as it is above.
              </span>
            </>
          )}
        </div>

        {/* Every failure says something, and what it can honestly say depends
            on which one it is. The one thing this dialog must never do is put
            the form back exactly as it was — that reads as "the button didn't
            work" on the most destructive operation in the panel, and the
            answer to that is to press it again.

            The NetworkBanner now raises itself on the same failure, and this
            plaque still earns its place: the banner says the API is out of
            reach, which is not the question in front of the operator. The
            question is what became of THIS player, and only the dialog that
            asked can answer it. */}
        {mutation.isError && (
          <div className={styles.errorBlurb} role="alert">
            {deleteError ? (
              <>
                Couldn&apos;t delete the player. Nothing was changed — the whole
                removal runs in one transaction.{" "}
                <span>{describeError(deleteError.code)}</span>
              </>
            ) : mutation.error instanceof NetworkError ? (
              // No promise about what happened: the request may have been
              // carried out and only the answer lost.
              <>
                Couldn&apos;t reach Social Hub API, so there is no telling
                whether the player went. Check the connection and try again —
                if they did go, the next attempt says so.
              </>
            ) : (
              // Neither the server's answer nor the transport — something
              // inside the panel broke. Naming the connection here would be a
              // guess, and this failure can even follow a delete that worked.
              <>
                Couldn&apos;t delete the player, and the failure says nothing
                about why — there is no telling whether they went. Reload the
                page to check.
              </>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <Modal
      open
      // In the dead end there is nothing to close back TO: the card behind
      // this dialog describes a player who no longer exists, and dismissing
      // onto it leaves the operator reading a stale page. So the ×, Escape and
      // the backdrop all do what the single button does — leave for the list.
      // `closeDisabled` is not the tool for this: Modal documents it as "a
      // request this dialog started is in flight", and here nothing is.
      onClose={alreadyGone ? () => onDeleted(false, null) : onClose}
      // While the delete is in flight there is no way out of the dialog, and
      // it says so: the × greys out, Escape and the backdrop do nothing. The
      // operation is not idempotent from the operator's side, so a second
      // attempt is not something to leave one keystroke away.
      closeDisabled={mutation.isPending}
      title={
        wanted === "" ? "Delete this player?" : `Delete ${player.display_name}?`
      }
      width="sm"
      footer={
        alreadyGone ? (
          <Button variant="primary" onClick={() => onDeleted(false, null)}>
            Back to players
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => mutation.mutate()}
              disabled={!canDelete}
              loading={mutation.isPending}
            >
              {mutation.isPending ? "Deleting…" : "Delete player"}
            </Button>
          </>
        )
      }
    >
      {body}
    </Modal>
  );
}

// === The breakdown =================================================== //

/**
 * Three sections, in the order an operator needs them: what happens to the
 * guild (an outcome, not a count), what goes with the player, and what stays
 * behind. The third matters as much as the second — somebody who deletes a
 * player and then meets their name in the chat history should have been told
 * beforehand.
 *
 * Zero counters draw no row, and a section with no rows is not drawn at all.
 */
function PreviewBreakdown({ preview }: { preview: PlayerDeletionPreview }) {
  // A disband takes the guild's whole chat with it, so this player's messages
  // are not among the things left behind — they are among the things removed,
  // and the "Kept" row about them must not be drawn.
  const disbanding = preview.guild?.outcome === "disband";
  const removed: ReactNode[] = [];
  if (preview.friendships > 0 || preview.friend_requests > 0) {
    removed.push(
      <Row key="friends" icon={<Users size={13} />}>
        {preview.friendships > 0 && (
          <>
            <span className={styles.num}>{preview.friendships}</span>{" "}
            {plural(preview.friendships, "friendship")}
          </>
        )}
        {preview.friendships > 0 && preview.friend_requests > 0 && " and "}
        {preview.friend_requests > 0 && (
          <>
            <span className={styles.num}>{preview.friend_requests}</span> pending
            friend {plural(preview.friend_requests, "request")}
          </>
        )}
      </Row>,
    );
  }
  if (preview.open_help_requests > 0) {
    removed.push(
      <Row key="help" icon={<MessageSquare size={13} />}>
        <span className={styles.num}>{preview.open_help_requests}</span> open
        help {plural(preview.open_help_requests, "request")} in the guild
      </Row>,
    );
  }
  if (preview.donations_out > 0) {
    removed.push(
      <Row key="donations" icon={<Gift size={13} />}>
        <span className={styles.num}>{preview.donations_out}</span>{" "}
        {plural(preview.donations_out, "donation")} sitting unclaimed in other
        players&apos; inboxes
      </Row>,
    );
  }
  // Gifts follow the donations, under the same 🎁 and by the same sentence:
  // the two are different mechanics with different tables behind them, but
  // what a deletion does to them is the same thing, and a second glyph would
  // draw a distinction the operator can do nothing with. They stay two rows
  // rather than one sum, because a sum is a number that can be checked against
  // nothing — the Gifts card on the page behind this dialog counts one of them.
  if (preview.gifts_out > 0) {
    removed.push(
      <Row key="gifts" icon={<Gift size={13} />}>
        <span className={styles.num}>{preview.gifts_out}</span>{" "}
        {plural(preview.gifts_out, "gift")} sitting unclaimed in other
        players&apos; inboxes
      </Row>,
    );
  }
  // And the third of the same kind. Its own row, and its own sentence, because
  // it is the one loss here the player did nothing to cause: the reward belongs
  // to whoever INVITED them, and it goes only because the pair that explains it
  // does.
  if (preview.referral_rewards_out > 0) {
    removed.push(
      <Row key="referrals" icon={<Gift size={13} />}>
        <span className={styles.num}>{preview.referral_rewards_out}</span>{" "}
        unclaimed referral {plural(preview.referral_rewards_out, "reward")}{" "}
        earned by the {plural(preview.referral_rewards_out, "player")} who
        invited them
      </Row>,
    );
  }
  // One slot, so a sentence rather than a count: the save's bytes go from
  // the bucket with the rows, and there is no version to fall back to.
  if (preview.has_cloud_save) {
    removed.push(
      <Row key="cloud-save" icon={<Cloud size={13} />}>
        The cloud save the game keeps for this player
      </Row>,
    );
  }
  if (preview.live_cycles > 0) {
    removed.push(
      <Row key="cycles" icon={<Trophy size={13} />}>
        Scores in <span className={styles.num}>{preview.live_cycles}</span> live
        leaderboard {plural(preview.live_cycles, "cycle")}
      </Row>,
    );
  }

  // No figure on this one, and no `> 0` gate either: what a disband deletes is
  // the guild's ENTIRE history, while `chat_messages` counts only this
  // player's own lines in it (docs/api.md). Printing that count here would
  // understate what goes, and gating on it would hide a whole guild's chat
  // from an operator deleting a leader who never posted.
  if (disbanding) {
    removed.push(
      <Row key="chat" icon={<MessageSquare size={13} />}>
        The guild&apos;s whole chat history — everybody&apos;s messages, not
        just this player&apos;s
      </Row>,
    );
  }

  const kept: ReactNode[] = [];
  if (preview.chat_messages > 0 && !disbanding) {
    kept.push(
      <Row key="chat" muted icon={<MessageSquare size={13} />}>
        <span className={styles.num}>{preview.chat_messages}</span> chat{" "}
        {plural(preview.chat_messages, "message")} — the author stops resolving
        to a name
      </Row>,
    );
  }
  if (preview.closed_cycles > 0) {
    kept.push(
      <Row key="archive" muted icon={<Archive size={13} />}>
        Places in closed leaderboard cycles — the archive is not rewritten
      </Row>,
    );
  }

  return (
    <>
      <div className={styles.section}>
        <div className={styles.label}>Guild</div>
        <GuildSection guild={preview.guild} />
      </div>

      {removed.length > 0 && (
        <div className={styles.section}>
          <div className={styles.label}>Also removed</div>
          <div className={styles.list}>{removed}</div>
        </div>
      )}

      {kept.length > 0 && (
        <div className={styles.section}>
          <div className={styles.label}>Kept</div>
          <div className={styles.list}>{kept}</div>
        </div>
      )}
    </>
  );
}

function GuildSection({ guild }: { guild: DeletionGuild | null }) {
  if (!guild) {
    return (
      <Row muted icon={<ShieldOff size={13} />}>
        Not in a guild.
      </Row>
    );
  }
  const isLeader = guild.role === "leader";
  return (
    <>
      <div className={styles.guild}>
        <div className={styles.guildName}>
          {guild.name}
          <Badge variant={isLeader ? "warn" : "neutral"}>
            {capitalize(guild.role)}
          </Badge>
        </div>
        <div className={styles.guildMeta}>{guildMeta(guild)}</div>
        {guild.outcome !== "leave" && (
          <div
            className={`${styles.outcome} ${
              guild.outcome === "disband" ? styles.disband : ""
            }`}
          >
            <span className={styles.arrow} aria-hidden>
              <ArrowRight size={13} />
            </span>
            {guild.outcome === "transfer" && guild.successor ? (
              <>
                <span>Leadership passes to</span>
                <span className={styles.successor}>
                  <Avatar name={guild.successor.display_name} size={20} />
                  {guild.successor.display_name}
                </span>
                <span className={styles.successorRole}>
                  — {successorLabel(guild.successor.role)}
                </span>
              </>
            ) : (
              <span>
                <b>The guild is deleted</b> — no living member left to lead it
              </span>
            )}
          </div>
        )}
      </div>
      {guild.outcome === "disband" && (
        <div className={styles.disbandNote}>
          <Alert variant="warn">
            Deleting the guild takes its help requests, invites and join
            requests with it — and its chat history too: nothing of the guild
            is left behind.
          </Alert>
        </div>
      )}
    </>
  );
}

/**
 * "12 members · 3 officers", or — when the guild is about to be dissolved —
 * what is left that cannot lead it. A bot never inherits the seat, so a roster
 * of bots is why a guild goes.
 */
function guildMeta(guild: DeletionGuild): string {
  const members = `${guild.member_count} ${plural(guild.member_count, "member")}`;
  if (guild.outcome === "disband") {
    return guild.bots > 0 ? `${members} · all bots` : `${members} · last member`;
  }
  if (guild.officers > 0) {
    return `${members} · ${guild.officers} ${plural(guild.officers, "officer")}`;
  }
  return members;
}

function successorLabel(role: string): string {
  return role === "officer" ? "senior officer" : "longest-serving member";
}

function Row({
  icon,
  muted,
  children,
}: {
  icon: ReactNode;
  muted?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`${styles.item} ${muted ? styles.muted : ""}`}>
      <span className={styles.ico} aria-hidden>
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}

/**
 * The dialog's shape while the preflight is in flight.
 *
 * The placeholder lines are a HEIGHT, not a count: nobody knows yet how many
 * rows the answer has. What they are worth is that the dialog does not jump
 * when it lands, so the number of them follows the list they stand in — which
 * gained a row today (friendships · help · donations · gifts · cycles), and
 * three lines now sit a row short of what an active player comes back with.
 */
function PreviewSkeleton() {
  return (
    <>
      <div className={styles.section}>
        <div className={styles.label}>Guild</div>
        <Skeleton height={58} radius={8} />
      </div>
      <div className={styles.section}>
        <div className={styles.label}>Also removed</div>
        <div className={styles.list}>
          <Skeleton width="78%" height={12} />
          <Skeleton width="64%" height={12} />
          <Skeleton width="70%" height={12} />
          <Skeleton width="66%" height={12} />
        </div>
      </div>
    </>
  );
}

function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
